// src/actions/auth_actions.js
import axios from "axios";
import {
  EMAIL_CHANGED,
  PASSWORD_CHANGED,
  LOGGED_IN_SUCCESS,
  LOGGED_IN_FAIL,
  SET_TOKEN,
  CLEAR_TOKEN,
  CLEAR_LOGIN_ERROR,
  CHANGE_PASSWORD_SUCCESS,
  CHANGE_PASSWORD_FAIL,
  CLEAR_CHANGE_PASSWORD_ERROR,
  FETCH_USERS_LOADING,
  FETCH_USERS_SUCCESS,
  FETCH_USERS_FAIL,
  UPDATE_USER_FLAG_LOADING,
  UPDATE_USER_FLAG_SUCCESS,
  UPDATE_USER_FLAG_FAIL,

  CREATE_USER_LOADING,
  CREATE_USER_SUCCESS,
  CREATE_USER_FAIL,
  DELETE_USER_LOADING,
  DELETE_USER_SUCCESS,
  DELETE_USER_FAIL,

  NOTIFY_CREDENTIALS_LOADING,
  NOTIFY_CREDENTIALS_SUCCESS,
  NOTIFY_CREDENTIALS_FAIL,

  FETCH_ROLES_LOADING,
  FETCH_ROLES_SUCCESS,
  FETCH_ROLES_FAIL,

  UPDATE_USER_ROLE_LOADING,
  UPDATE_USER_ROLE_SUCCESS,
  UPDATE_USER_ROLE_FAIL,

  //Manage Roles
  FETCH_PERMISSIONS_LOADING,
  FETCH_PERMISSIONS_SUCCESS,
  FETCH_PERMISSIONS_FAIL,
  FETCH_ROLE_PERMISSIONS_LOADING,
  FETCH_ROLE_PERMISSIONS_SUCCESS,
  FETCH_ROLE_PERMISSIONS_FAIL,
  REPLACE_ROLE_PERMISSIONS_LOADING,
  REPLACE_ROLE_PERMISSIONS_SUCCESS,
  REPLACE_ROLE_PERMISSIONS_FAIL,

  CREATE_ROLE_LOADING,
  CREATE_ROLE_SUCCESS,
  CREATE_ROLE_FAIL,

  DELETE_ROLE_LOADING,
  DELETE_ROLE_SUCCESS,
  DELETE_ROLE_FAIL,

} from "./types";

// --- Helpers
const mapToPermissionObjects = (items, master = []) => {
  // Accepts either [{id,code,...}] or [id, id, ...] and returns [{id,code,description}]
  return (items || []).map((p) => {
    if (typeof p === 'number') {
      return master.find(m => Number(m.id) === p) || { id: p };
    }
    if (p && typeof p === 'object') return p;
    return { id: p };
  });
};

// Delete a role by id (backend also reassigns users with that role to Role_Id=1)
export const deleteRole = (roleId) => async (dispatch) => {
  const rid = Number(roleId);
  dispatch({ type: DELETE_ROLE_LOADING, payload: { roleId: rid } });

  try {
    const API_URL = process.env.REACT_APP_ADMIN_API_ADDRESS;
    const token   = localStorage.getItem("jwtToken");

    const { data } = await axios.delete(
      `${API_URL}/secureapi/analytics/roles/${rid}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );

    // Optimistic local consistency: remove role from roles array
    // and update any users in state to Role_Id = 1
    dispatch({
      type: DELETE_ROLE_SUCCESS,
      payload: { roleId: rid }
    });

    return data; // { success, message, deletedRoleId, affectedUsers, removedRolePermissions }
  } catch (error) {
    const message = error?.response?.data?.message || error.message;
    dispatch({ type: DELETE_ROLE_FAIL, payload: { roleId: rid, message } });
    throw error;
  }
};

// Create a new role
export const createRole = (name, description = "") => async (dispatch) => {
  dispatch({ type: CREATE_ROLE_LOADING, payload: { name } });

  try {
    const API_URL = process.env.REACT_APP_ADMIN_API_ADDRESS;
    const token   = localStorage.getItem("jwtToken");

    const { data } = await axios.post(
      `${API_URL}/secureapi/analytics/roles`,
      { name, description },
      { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } }
    );

    // API is designed to return: { success: true, role: { id, name, description } }
    const role = data?.role || data?.data;
    if (!role) throw new Error(data?.message || "Failed to create role");

    dispatch({ type: CREATE_ROLE_SUCCESS, payload: role });
    return role;
  } catch (error) {
    const message = error?.response?.data?.message || error.message;
    dispatch({ type: CREATE_ROLE_FAIL, payload: message });
    throw error;
  }
};

// --- Fetch ALL permissions
export const fetchPermissions = () => async (dispatch) => {
  dispatch({ type: FETCH_PERMISSIONS_LOADING });
  try {
    const API_URL = process.env.REACT_APP_ADMIN_API_ADDRESS;
    const token   = localStorage.getItem('jwtToken');
    const { data } = await axios.get(
      `${API_URL}/secureapi/analytics/permissions`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    // Expecting { success:true, permissions:[{id,code,description}...] }
    dispatch({ type: FETCH_PERMISSIONS_SUCCESS, payload: data.permissions || [] });
    return data.permissions || [];
  } catch (error) {
    const message = error?.response?.data?.message || error.message;
    dispatch({ type: FETCH_PERMISSIONS_FAIL, payload: message });
    throw error;
  }
};

// --- Fetch permissions for a specific role
export const fetchRolePermissions = (roleId) => async (dispatch, getState) => {
  dispatch({ type: FETCH_ROLE_PERMISSIONS_LOADING, payload: { roleId } });
  try {
    const API_URL = process.env.REACT_APP_ADMIN_API_ADDRESS;
    const token   = localStorage.getItem('jwtToken');
    const { data } = await axios.get(
      `${API_URL}/secureapi/analytics/roles/${roleId}/permissions`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    // API may return numbers or objects -> normalize using master
    const master = getState()?.auth?.permissionsMaster || [];
    const permissions = mapToPermissionObjects(data.permissions || [], master);

    dispatch({
      type: FETCH_ROLE_PERMISSIONS_SUCCESS,
      payload: { roleId: Number(roleId), permissions }
    });
    return permissions;
  } catch (error) {
    const message = error?.response?.data?.message || error.message;
    dispatch({ type: FETCH_ROLE_PERMISSIONS_FAIL, payload: message });
    throw error;
  }
};

// --- Replace a role's permissions
export const replaceRolePermissions = (roleId, permissionIds = []) => async (dispatch, getState) => {
  dispatch({ type: REPLACE_ROLE_PERMISSIONS_LOADING, payload: { roleId } });
  try {
    const API_URL = process.env.REACT_APP_ADMIN_API_ADDRESS;
    const token   = localStorage.getItem('jwtToken');

    // PUT body shape the API expects: { permissionIds: [1,2,3] }
    await axios.put(
      `${API_URL}/secureapi/analytics/roles/${roleId}/permissions`,
      { permissionIds },
      { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
    );

    // Optimistic local update (normalize to objects using master)
    const master = getState()?.auth?.permissionsMaster || [];
    const updated = mapToPermissionObjects(permissionIds, master);

    dispatch({
      type: REPLACE_ROLE_PERMISSIONS_SUCCESS,
      payload: { roleId: Number(roleId), permissions: updated }
    });

    // Optionally re-fetch from server for absolute truth:
    // await dispatch(fetchRolePermissions(roleId));

    return updated;
  } catch (error) {
    const message = error?.response?.data?.message || error.message;
    dispatch({ type: REPLACE_ROLE_PERMISSIONS_FAIL, payload: message });
    throw error;
  }
};


// Update a user's Role_Id by email
export const updateUserRole = (email, roleId) => async (dispatch) => {
  dispatch({ type: UPDATE_USER_ROLE_LOADING, payload: { email } });
  try {
    const API_URL = process.env.REACT_APP_ADMIN_API_ADDRESS;
    const token   = localStorage.getItem("jwtToken");

    const { data } = await axios.put(
      `${API_URL}/secureapi/analytics/user/role`,
      { email, roleId },
      { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } }
    );

    // Option A: optimistic update users list to reflect new Role_Id
    dispatch({
      type: UPDATE_USER_ROLE_SUCCESS,
      payload: { email, roleId: Number(roleId) }
    });

    // Option B (alternative): re-fetch users for absolute truth
    // await dispatch(fetchUsers());

    return data;
  } catch (error) {
    const message = error?.response?.data?.message || error.message;
    dispatch({ type: UPDATE_USER_ROLE_FAIL, payload: message });
    throw error;
  }
};

// Fetch all roles
export const fetchRoles = () => async (dispatch) => {
  dispatch({ type: FETCH_ROLES_LOADING });
  try {
    const API_URL = process.env.REACT_APP_ADMIN_API_ADDRESS;
    const token   = localStorage.getItem("jwtToken");

    const { data } = await axios.get(
      `${API_URL}/secureapi/analytics/roles`,
      { headers: { Authorization: `Bearer ${token}` } }
    );

    // API returns: { success: true, roles: [...] }
    dispatch({ type: FETCH_ROLES_SUCCESS, payload: data.roles || [] });
    return data.roles || [];
  } catch (error) {
    const message = error?.response?.data?.message || error.message;
    dispatch({ type: FETCH_ROLES_FAIL, payload: message });
    throw error;
  }
};

//Notify Users
export const notifyUserCredentials = (email) => async (dispatch) => {
  dispatch({ type: NOTIFY_CREDENTIALS_LOADING, payload: { email } });

  try {
    const token = localStorage.getItem("jwtToken");
    const API_URL = process.env.REACT_APP_ADMIN_API_ADDRESS;

    const { data } = await axios.post(
      `${API_URL}/secureapi/analytics/notify-credentials`,
      { email },
      { headers: { Authorization: `Bearer ${token}` } }
    );

    dispatch({ type: NOTIFY_CREDENTIALS_SUCCESS, payload: data.message });
    return data.message;
  } catch (error) {
    const message = error.response?.data?.message || error.message;
    dispatch({ type: NOTIFY_CREDENTIALS_FAIL, payload: message });
    throw error;
  }
};

//Clear Password Error
export const clearChangePasswordError = () => {
  return {
    type: CLEAR_CHANGE_PASSWORD_ERROR,
  };
};


// Action to update email in Redux state
export const emailChanged = (email) => ({
  type: EMAIL_CHANGED,
  payload: email,
});

// Action to update password in Redux state
export const passwordChanged = (password) => ({
  type: PASSWORD_CHANGED,
  payload: password,
});

// Action to clear the current login error (used after the toast is dismissed)
export const clearLoginError = () => ({
  type: CLEAR_LOGIN_ERROR,
});


export const createUser = (email, password, roleId) => async (dispatch) => {
  dispatch({ type: CREATE_USER_LOADING, payload: { email } });
  try {
    const token = localStorage.getItem("jwtToken");
    const API_URL = process.env.REACT_APP_ADMIN_API_ADDRESS;

    const { data } = await axios.post(
      `${API_URL}/secureapi/analytics/register`,
      { email, password, roleId }, // <-- send roleId
      { headers: { Authorization: `Bearer ${token}` } }
    );

    // Ensure Role_Id is present in the returned object (fallback to roleId we sent)
    const created = {
      ...(data?.data || {}),
      Role_Id: (data?.data?.Role_Id ?? roleId)
    };

    dispatch({ type: CREATE_USER_SUCCESS, payload: created });
    return created;
  } catch (error) {
    const message = error.response?.data?.message || error.message;
    dispatch({ type: CREATE_USER_FAIL, payload: message });
    throw error;
  }
};


export const deleteUserByEmail = (email) => async (dispatch) => {
  dispatch({ type: DELETE_USER_LOADING, payload: { email } });
  try {
    const token = localStorage.getItem("jwtToken");
    const API_URL = process.env.REACT_APP_ADMIN_API_ADDRESS;

    await axios.delete(
      `${API_URL}/secureapi/analytics/user`,
      {
        headers: { Authorization: `Bearer ${token}` },
        data: { deleteUserData: { email } }
      }
    );

    dispatch({ type: DELETE_USER_SUCCESS, payload: { email } });
    return email;
  } catch (error) {
    const message = error.response?.data?.message || error.message;
    dispatch({ type: DELETE_USER_FAIL, payload: message });
    throw error;
  }
};

// Update a user’s activeFlag
export const updateUserFlag = (userId, activeFlag) => async (dispatch) => {
  dispatch({ type: UPDATE_USER_FLAG_LOADING, payload: userId });
  try {
    const apiUrl = process.env.REACT_APP_ADMIN_API_ADDRESS;
    const token  = localStorage.getItem('jwtToken');
    const response = await axios.put(
      `${apiUrl}/secureapi/analytics/users/${userId}/active-flag`,
      { activeFlag },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    dispatch({
      type: UPDATE_USER_FLAG_SUCCESS,
      payload: { userId, activeFlag }
    });
  } catch (err) {
    dispatch({
      type: UPDATE_USER_FLAG_FAIL,
      payload: err.response?.data?.message || err.message
    });
  }
};

// Fetch all users
export const fetchUsers = () => async (dispatch) => {
  dispatch({ type: FETCH_USERS_LOADING });
  try {
    const apiUrl = process.env.REACT_APP_ADMIN_API_ADDRESS;
    const token  = localStorage.getItem('jwtToken');
    const response = await axios.get(
      `${apiUrl}/secureapi/analytics/users`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    dispatch({
      type: FETCH_USERS_SUCCESS,
      payload: response.data.data   // note your router returns `{ success:true, data: users }`
    });
  } catch (err) {
    dispatch({
      type: FETCH_USERS_FAIL,
      payload: err.response?.data?.message || err.message
    });
  }
};

// Login action calling the API and handling errors
export const Login = (email, password) => async (dispatch) => {
  const apiUrl = process.env.REACT_APP_ADMIN_API_ADDRESS;
  const fullUrl = `${apiUrl}/api/analytics/login`;

  try {
    const { data } = await axios.post(fullUrl, { email, password });

    if (!data.success) {
      return dispatch({ type: LOGGED_IN_FAIL, payload: { error: data.message } });
    }

    const { token, user, role, permissions } = data;

    // Persist minimal things (token + email). You *can* persist permissions for quicker hydration.
    localStorage.setItem("jwtToken", token);
    localStorage.setItem("email", user.email);
    localStorage.setItem("adminFlag", user.adminFlag);
    localStorage.setItem("permissions", JSON.stringify(permissions || [])); // optional
    localStorage.setItem("role", JSON.stringify(role || null));            // optional

    dispatch({ type: LOGGED_IN_SUCCESS, payload: { user, role, permissions } });
    dispatch({ type: SET_TOKEN, payload: token });
  } catch (error) {
    const errMsg = error.response?.data?.message || error.message;
    dispatch({ type: LOGGED_IN_FAIL, payload: { error: errMsg } });
  }
};

//Change Password Action
export const changePassword = (password, newPassword, newPassword2) => async (dispatch) => {
  const apiUrl = `${process.env.REACT_APP_ADMIN_API_ADDRESS}/secureapi/analytics/change-password`;
  
  // Validate that newPassword and newPassword2 match
  if (newPassword !== newPassword2) {
    dispatch({ type: CHANGE_PASSWORD_FAIL, payload: "New passwords do not match!" });
    return;
  }

  try {
    const token = localStorage.getItem("jwtToken");
    const email = localStorage.getItem("email");

    if (!token || !email) {
      dispatch({ type: CHANGE_PASSWORD_FAIL, payload: "Missing authentication token or email." });
      return;
    }

    const response = await axios.post(
      apiUrl,
      { email, password, newPassword },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );

    const { data } = response;

    if (data.success) {
      dispatch({ type: CHANGE_PASSWORD_SUCCESS, payload: data.message });
    } else {
      dispatch({ type: CHANGE_PASSWORD_FAIL, payload: data.message });
    }
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message;
    dispatch({ type: CHANGE_PASSWORD_FAIL, payload: errorMessage });
  }
};


// Logout action clears the token and resets authentication state
export const Logout = () => (dispatch) => {
  localStorage.removeItem("jwtToken"); // Clear the token
  localStorage.removeItem("email"); // Clear User's Email
  localStorage.removeItem("permissions");
  localStorage.removeItem("role");
  dispatch({ type: CLEAR_TOKEN });
  dispatch({ type: LOGGED_IN_FAIL, payload: { error: null } });
};

// ---------------------------------------------------------------------------
// Google Workspace SSO login (barsys domains only).
// Sends the Google ID token to the backend, which verifies it, enforces the
// barsys domain allowlist, and returns the same token/user/role/permissions
// payload as the password login.
// ---------------------------------------------------------------------------
export const GoogleLogin = (credential) => async (dispatch) => {
  const apiUrl = process.env.REACT_APP_ADMIN_API_ADDRESS;
  try {
    const { data } = await axios.post(`${apiUrl}/api/analytics/login/google`, { credential });
    if (!data.success) {
      return dispatch({ type: LOGGED_IN_FAIL, payload: { error: data.message } });
    }
    const { token, user, role, permissions } = data;
    localStorage.setItem("jwtToken", token);
    localStorage.setItem("email", user.email);
    localStorage.setItem("adminFlag", user.adminFlag);
    localStorage.setItem("permissions", JSON.stringify(permissions || []));
    localStorage.setItem("role", JSON.stringify(role || null));
    dispatch({ type: LOGGED_IN_SUCCESS, payload: { user, role, permissions } });
    dispatch({ type: SET_TOKEN, payload: token });
  } catch (error) {
    const errMsg = error.response?.data?.message || error.message;
    dispatch({ type: LOGGED_IN_FAIL, payload: { error: errMsg } });
  }
};
